import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { EnrichmentPostStepFlags } from '~/types/enrichment-flags';

/**
 * Post-step flags an operator chose when launching an experiment, keyed by
 * experiment id.
 *
 * Kept in its own store rather than on the wizard store because the two have
 * different lifetimes: the wizard resets the moment the experiment launches,
 * while sample enrichment and the full-catalog pipeline are triggered later from
 * the funnel — different pages, often after a reload. Keyed by id rather than
 * holding a single "current" value so two experiments in flight in separate tabs
 * can't read each other's configuration.
 *
 * The experiment record has no field for this yet, so this is the only place the
 * choice survives. See the TODO(BE) on `ContentScoringTriggerPayload`: once the
 * backend persists the flags against the experiment, every later trigger inherits
 * them server-side and this store can be deleted.
 */

/** How many experiments' choices to retain. Bounded because entries are never
 *  explicitly removed — an experiment is launched and the operator moves on. */
const MAX_TRACKED = 20;

interface ExperimentPostStepFlagsState {
  /** expId → the flags that experiment was launched with. */
  byExperiment: Record<string, EnrichmentPostStepFlags>;
  /** Insertion order, oldest first — drives eviction past {@link MAX_TRACKED}. */
  order: string[];
  setFlags: (expId: string, flags: EnrichmentPostStepFlags) => void;
}

export const useExperimentPostStepFlagsStore = create<ExperimentPostStepFlagsState>()(
  persist(
    (set) => ({
      byExperiment: {},
      order: [],
      setFlags: (expId, flags) =>
        set((state) => {
          // Re-launching the same experiment moves it to the end rather than
          // duplicating the entry.
          const order = [...state.order.filter((id) => id !== expId), expId].slice(-MAX_TRACKED);
          const kept = new Set(order);
          // Rebuilt by filtering rather than deleting keys — evicts anything the
          // trimmed order dropped, in one pass.
          const byExperiment = Object.fromEntries(
            Object.entries({ ...state.byExperiment, [expId]: flags }).filter(([id]) =>
              kept.has(id),
            ),
          );
          return { byExperiment, order };
        }),
    }),
    {
      name: 'experiment-post-step-flags',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

/**
 * Reads the flags an experiment was launched with, as a spreadable object.
 *
 * Returns `{}` when nothing was recorded — an experiment launched before this
 * shipped, or resumed in a different browser. That spreads to no keys, so the
 * server default applies rather than an accidental all-false.
 */
export function readExperimentPostStepFlags(expId: string): EnrichmentPostStepFlags {
  return useExperimentPostStepFlagsStore.getState().byExperiment[expId] ?? {};
}
